import { gatewayClient } from 'utils/gatewayClient';

import { PretaxCommuterEnrollStatus } from 'constants/accounts';
import { DirectDepositStatus } from 'constants/claims';

export const PLAID_ENV = process.env.REACT_APP_PLAID_ENVIRONMENT;
export const PLAID_PUBLIC_KEY = process.env.REACT_APP_PLAID_PUBLIC_KEY;

export type WalletRenewFrequency =
  | 'never'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'annually';

export interface FetchProfileResponse {
  employee: {
    activated_date?: string;
    company_detail: {
      features: {
        marketplace: boolean;
        pretax_config: { pretax_enabled: boolean };
        credit_card: boolean;
        marketplace_international: boolean;
      };
      settings: {
        stripe_physical_card_enabled: boolean;
        stripe_virtual_card_enabled: boolean;
      };
    };
    date_of_birth?: string;
    avatar_url: string;
    company: { id: string };
    company_id: string;
    created: string;
    updated: string;
    email: string;
    employee_id: string;
    employee_wallets: Array<{
      amount: number;
      company_id: string;
      company_wallet_configuration_id: string;
      company_wallet_configuration: {
        active: boolean;
        allow_year_end_rollover: boolean;
        blacklisted_vendors: Array<string>;
        categories: Array<{
          id: string;
          name: string;
          alias: string;
          subcategories: Array<{
            aliases: Array<string>;
            eligible: boolean;
            name: string;
            value: string;
            // FIXME: Experimental property
            examples?: Array<{
              name: string;
              value: string;
            }>;
          }>;
          is_twic_store_enabled?: boolean;
        }>;
        company_id: string;
        created: string;
        is_date_based_wallet: boolean;
        end_date: string;
        frequency_offset_months: number;
        id: string;
        is_prorate_wallet_creation: boolean;
        is_twic_card_payment_allowed: boolean;
        is_twic_store_enabled?: boolean;
        direct_deposit_status?: DirectDepositStatus;
        priority: number;
        renew_frequency: WalletRenewFrequency;
        renewal_default_config: { lifetime_max_amount: number };
        runout_period?: number;
        start_date: string;
        wallet_description: string;
        wallet_name: string;
        wallet_type_id: string;
        whitelisted_vendors: string[];
      };
      created: string;
      employee_id: string;
      funded_by?: string;
      grace_period_end_date?: string;
      id: string;
      is_employee_eligible: boolean; // Flag to indicate if the employee is eligible for the company wallet
      last_renew_amount: number;
      next_credit_date: string;
      next_renew_amount_loss: number;
      next_reset_date: string;
      runout_balance?: {
        start_date: string;
        end_date: string;
        amount: number;
      };
      status?: string;
      stipend_config: {
        amount: number;
        max_amount: number;
        amount_to_points_ratio?: number;
        disabled_features?: {
          reimbursement?: boolean;
        };
        renew_amount: number;
      };
      wallet_type: {
        created: string;
        default_categories: string[];
        description: string;
        id: string;
        name: string;
      };
      wallet_type_id: string;
      visible_to_employee: boolean; // Flag to indicate if the wallet is active wallet or inactive wallet
      election_details?: {
        employee_annual_election: number;
        employer_annual_election: number;
        employee_payperiod_election: number;
        employee_posttax_payperiod_election: number;
        employer_payperiod_election: number;
      };
      points_available: number;
      points_expiring: number;
    }>;
    preferred_first_name?: string;
    preferred_last_name?: string;
    first_name: string;
    full_name: string;
    hmac: string;
    id: string;
    is_profile_complete: boolean;
    is_terminated: boolean;
    is_waiver_completed: boolean;
    last_name: string;
    personal_email?: string;
    phone: string;
    points_available: number;
    settings: {
      is_terminated_pretax?: boolean;
      address?: {
        country: string;
        locality: string;
        state: string;
        street: string;
        street_ext: string;
        zip: string;
      };
      alegeus_participant_id: string;
      connected_account_status: string;
      country: string;
      currency: string;
      currency_symbol: string;
      employee_country_exchage_rate: number;
      external_payment: {
        direct_deposit_account?: {
          status: string;
        };
        connected_account?: {
          status: string;
          last4?: string;
          account_type_code?: string;
          bank_account_name?: string;
          verification_method?: string;
          institution: { name: string; institution_id?: string };
        };
        accounts: Array<{
          account_details: {
            id: string;
            mask: string;
            name: string;
            subtype: string;
            type: string;
          };
          institution: {
            institution_id: string;
            name: string;
          };
          status: string;
          verification_method: string;
        }>;
      };
      connected_account: {
        account_type_code: string;
        bank_account_name: string;
        institution: {
          name: string;
          institution_id: string;
        };
        last4: string;
        status: string;
        verification_method: string;
      };
      charge_cc_for_ineligible_purchase: boolean;
      employee_can_make_backup_payments: boolean;
      flex_mode_enabled: boolean;
      has_connected_account: boolean;
      hsa_transfer_enabled: boolean;
      is_cdh_enabled: boolean;
      is_posttax_enabled: boolean;
      is_pretax_enabled?: boolean;
      location_id: string;
      payment: {
        token?: string;
        last4?: string;
      } | null;
      pretax_commuter_status: PretaxCommuterEnrollStatus;
      stripe_cardholder_exists: boolean;
      stripe_virtual_card_enabled: boolean;
      travel_mode_enabled: boolean;
      wallet_overdraft_enabled: boolean;
      wallet_overdraft_limit: number;
      slack_connection?: string;
      primary_residence: {
        country?: string;
        street?: string;
        street_ext?: string;
        locality?: string;
        state?: string;
        zip?: string;
      };
    };
    show_signin_banner: boolean;
    impersonated: boolean; // Flag to indicate if the employee is impersonated by Forma support
    miso_user_hash: string;
  };
  company: {
    company_info: {
      common_name: string;
      logo_url: string;
      secondary_logo_url: string;
    };
    company_wallet_configurations: Array<{
      active: boolean;
      allow_year_end_rollover: boolean;
      blacklisted_vendors: Array<string>;
      categories: Array<{
        id: string;
        name: string;
        alias: string;
        subcategories: Array<{
          aliases: Array<string>;
          eligible: boolean;
          name: string;
          value: string;
          // FIXME: Experimental property
          examples?: Array<{
            name: string;
            value: string;
          }>;
        }>;
      }>;
      company_id: string;
      is_date_based_wallet: boolean;
      created: string;
      end_date: string;
      frequency_offset_months: number;
      id: string;
      is_prorate_wallet_creation: boolean;
      is_twic_card_payment_allowed: boolean;
      is_twic_store_enabled?: boolean;
      priority: number;
      renew_frequency: WalletRenewFrequency;
      renewal_default_config: { lifetime_max_amount: number };
      start_date: string;
      wallet_description: string;
      wallet_name: string;
      wallet_type_id: string;
      whitelisted_vendors: string[];
    }>;
    features: {
      benefit_bank_account: boolean;
      credit_card: boolean;
      direct_deposit: boolean;
      eligibility_search: boolean;
      event: boolean;
      external_payments: boolean;
      group: boolean;
      location_configuration: boolean;
      marketplace: boolean;
      marketplace_international: boolean;
      pretax_config: { twic_commuter: boolean; pretax_enabled: boolean };
      reimbursement: boolean;
      reimbursement_config: {
        support_multi_month: boolean;
        allow_year_end_rollover: boolean;
      };
      stipend: boolean;
      stipend_config: {
        amount: number;
        amount_to_points_ratio: number;
        cycle: string;
        display_as_amount: boolean;
        max_amount: number;
        reminder_email: boolean;
        strict: boolean;
      };
      wallet_overdraft: boolean;
    };
    id: string;
    settings: {
      skip_onboarding: boolean;
      reimbursement_policy_link: string;
      sso: {
        idp_entity_id: string;
        idp_metadata_url: string;
        use_gateway_sso_entity: boolean;
      };
      slack: boolean;
      color_primary: string;
      location: Array<{
        country: string;
        country_code: string;
        currency: string;
        currency_symbol: string;
        id: string;
        name: string;
      }>;
      stripe_physical_card_enabled: boolean;
      stripe_virtual_card_enabled: boolean;
      wallet_excluded_categories?: Array<string>;
      is_receipt_required: boolean;
      /**
       * Hsa bulk transfer metadata
       */
      hsaBulkTransfer?: {
        /**
         * @deprecated
         * Instead of isEligible being determine by the backend, the client will now determine
         * if a member is eligible based on the optInStartDate and transferDeadline values
         */
        isEligible: boolean;
        /**
         * The name of the customers former hsa custodian e.g. "Navia"
         */
        custodianName: string;
        /**
         * The first day the member can opt in to the bulk transfer flow
         */
        optInStartDate: string;
        /**
         * The last day the member can opt in to the bulk transfer flow
         */
        transferDeadline: string;
        /**
         * The day we expect the funds to transfer from the former custodian to Forma
         */
        fundsTransferDate: string;
        /**
         * A link to a notion page that explains the terms and conditions for the bulk transfer.
         * Relevant only to non-alegeus custodians.
         */
        termsConditionsLink: string;
      };
    };
  };
}

export function fetchProfile() {
  return gatewayClient.get<FetchProfileResponse>('/api/v2/settings/profile');
}

export function fetchProfileV3() {
  return gatewayClient.get<FetchProfileResponse>('/api/v3/settings/profile');
}

export function fetchPaymentToken() {
  return gatewayClient.get('/api/v2/settings/payment-token');
}

export function updatePaymentToken({ nonce }: $TSFixMe) {
  return gatewayClient.post('/api/v2/settings/payment-token', { nonce });
}

export function onCreditCardDeleted() {
  return gatewayClient.patch(`api/v2/settings/credit-card`);
}

export function updateProfile(data: $TSFixMe) {
  return gatewayClient.post('/api/v2/settings/profile', data);
}

export function changePassword({
  password,
  new_password,
  confirm_password,
  reset_token,
}: {
  password?: string;
  new_password: string;
  confirm_password: string;
  reset_token: string;
}) {
  return gatewayClient.put('/api/v2/settings/password', {
    password,
    new_password,
    confirm_password,
    reset_token,
  });
}

export function fetchCard(card_id: $TSFixMe) {
  return gatewayClient.get(`/api/v2/twic-cards/${card_id}`);
}

export interface FetchCardsResponse {
  cards: Array<{
    brand: string;
    cardholder: {
      billing: {
        address: {
          city: string;
          country: string;
          line1: string;
          line2: string | null;
          postal_code: string;
          state: string;
        };
      };
      id: string;
      name: string;
      status: string;
      type: string;
    };
    created: number;
    currency: string;
    cvc: string;
    exp_month: number;
    exp_year: number;
    id: string;
    number: string;
    last4: string;
    livemode: boolean;
    metadata: {
      company_id: string;
      email: string;
      employee_id: string;
      name: string;
    };
    status: string;
    type: string;
  }>;
}

export function fetchCards() {
  return gatewayClient.get<FetchCardsResponse>('/api/v2/twic-cards');
}

export function fetchCardholder() {
  return gatewayClient.get('/api/v2/twic-cardholder');
}

export function createTwicCard({
  first_name,
  last_name,
  phone,
  billing_address,
  shipping_address,
  should_create_physical_card,
  should_create_virtual_card,
}: $TSFixMe) {
  return gatewayClient.post('/api/v2/twic-cards', {
    first_name,
    last_name,
    phone,
    billing_address,
    shipping_address,
    should_create_physical_card,
    should_create_virtual_card,
  });
}

export function replaceStripeCard({
  shipping_address: { city, line1, line2, postal_code, state, country },
  cancellation_reason,
  card_id,
}: $TSFixMe) {
  return gatewayClient.post(`/api/v2/twic-cards/${card_id}/replace`, {
    shipping_address: {
      city,
      line1,
      line2,
      postal_code,
      state,
      country,
    },
    cancellation_reason,
  });
}

export function updateTwicCard({ card_id, status }: $TSFixMe) {
  return gatewayClient.put(`/api/v2/twic-cards/${card_id}`, {
    status,
  });
}

export function fetchAllEmployees({
  limit,
  page,
  basic_info,
}: {
  limit?: number;
  page?: number;
  basic_info?: boolean;
}) {
  return gatewayClient.get('/api/v2/company/employees', {
    params: { limit, page, basic_info },
  });
}

export function getLocationbyIP(params: $TSFixMe) {
  return gatewayClient.get('/api/v2/settings/ip-location', {
    params,
  });
}

export function setSlackToken(data: $TSFixMe) {
  return gatewayClient.post('/api/v2/settings/slack-auth-token', data);
}

export function createManualConnectedAccount({
  bank_token_id,
  account_number,
  account_type_code,
}: {
  bank_token_id?: string;
  account_number: string;
  account_type_code: string;
}) {
  return gatewayClient.post('/api/v2/connected-accounts/manual', {
    bank_token_id,
    account_number,
    account_type_code,
  });
}

export function getConnectedAccountVerificationLink() {
  return gatewayClient.get('/api/v2/connected-accounts/verify');
}

type ShippingAddress = {
  country?: string;
  street?: string;
  street_ext?: string;
  locality: string;
  state: string;
  zip: string;
};

interface UpdateEmployeeSettings {
  flex_mode_enabled?: boolean;
  travel_mode_enabled?: boolean;
  wallet_overdraft_enabled?: boolean;
  charge_cc_for_ineligible_purchase?: boolean;
  shipping_address?: ShippingAddress;
}

export interface FetchNotificationSettings {
  notification_preferences: Array<{
    company_id: string;
    created: string;
    employee_id: string;
    enabled_channels: {
      email: boolean;
      push: boolean;
      slack: boolean;
    };
    id: string;
    type: string;
    updated: string;
  }>;
}

export function updateEmployeeSettings({
  shipping_address,
  flex_mode_enabled,
  travel_mode_enabled,
  wallet_overdraft_enabled,
  charge_cc_for_ineligible_purchase,
}: UpdateEmployeeSettings) {
  return gatewayClient.post('/api/v2/settings/profile', {
    flex_mode_enabled,
    travel_mode_enabled,
    shipping_address,
    wallet_overdraft_enabled,
    charge_cc_for_ineligible_purchase,
  });
}

export function createConnectedAccount(
  public_token: string,
  account: { id: string },
  institution: { name?: string; institution_id?: string }
) {
  return gatewayClient.post('/api/v2/connected-accounts/automatic', {
    public_token,
    account,
    institution,
  });
}

export function activateConnectedAccount() {
  return gatewayClient.put('/api/v2/connected-accounts/activate');
}

export function getNotificationSettings() {
  return gatewayClient.get<FetchNotificationSettings>(
    `/api/v2/settings/notifications`
  );
}

export function updateNotificationSettings(notification: $TSFixMe) {
  return gatewayClient.put(`/api/v2/settings/notifications`, notification);
}

export function updateCardholder({ billing_address }: $TSFixMe) {
  return gatewayClient.put(`/api/v2/twic-cardholder`, {
    billing_address,
  });
}
